/* Low-level codec helpers for share links: raw DEFLATE + URL-safe base64.
 *
 * Uses 'deflate-raw' (no zlib/gzip header or checksum) which is ~18 bytes —
 * about 24 base64url chars — shorter per link than gzip, a meaningful win on
 * small/medium snippets where the header dominates. Both the Streams APIs and
 * btoa/atob are available in modern browsers and the Node 18+ test runtime, so
 * this module is environment-agnostic (no DOM refs). */

// Blob.stream() avoids WritableStreamDefaultWriter generic-buffer typing
// friction and works the same in browsers and Node 18+.
async function pipeThrough(input: Uint8Array, transform: GenericTransformStream): Promise<Uint8Array> {
  const stream = new Blob([input as BufferSource]).stream().pipeThrough(transform);
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

export function deflateRaw(input: Uint8Array): Promise<Uint8Array> {
  return pipeThrough(input, new CompressionStream('deflate-raw'));
}

export function inflateRaw(input: Uint8Array): Promise<Uint8Array> {
  return pipeThrough(input, new DecompressionStream('deflate-raw'));
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  // Chunked to avoid call-stack limits on large payloads.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
